// src/modules/app-views/ticketEvents/pages/Document/modals/SaleTicketModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { Alert, Descriptions, Form, InputNumber, Row } from 'antd';
import { ShoppingCartOutlined } from '@ant-design/icons';
import { createRoot } from 'react-dom/client';
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';
import translateX from 'utils/translateX';
import financialService from 'services/financialService';
import appService from 'services/appService';
import GenericModal from 'components/core/GenericModal';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import SimpleSlider from 'components/SimpleComponents/SimpleSlider';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import RecipientCard from 'modules/app-views/invoices/pages/Document/tabs/RecordTab/RecipientCard';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SaleTicketModal = ({ show, setShow, data, onSuccess }) => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  const eventId = data?._id;
  const eventName = data?.name || '-';
  const currentPrice = data?.currentPrice ?? data?.minimumPrice ?? 0;
  const totalTickets = data?.totalTickets || 0;
  const soldTickets = data?.soldTickets || 0;
  const remaining = Math.max(0, totalTickets - soldTickets);
  const isAvailable = data?.status === 'active';

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const handleFinish = async ({ form }) => {
    const values = form.getFieldsValue(true);
    const entityId = values.buyerId;
    const quantity = values.quantity || 1;
    const note = values.note;

    if (!entityId) {
      appService.message({ flag: 'w', text: 'select_buyer_first' });
      return false;
    }

    try {
      await axiosService.post({
        url: API.TICKET_SALES.URL.replace('/:_id?', ''),
        data: { eventId, entityId, quantity, note },
      });
      appService.notification({ flag: 's', title: 'sale_confirmed' });
      onSuccess?.();
    } catch (err) {
      const msg =
        err?.response?.data?.data?.message || translateX('error_selling_ticket');
      appService.message({ flag: 'e', text: msg });
      return false;
    }
  };

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------
  const steps = useMemo(
    () => [
      {
        content: () => (
          <SaleTicketFormContent
            eventName={eventName}
            currentPrice={currentPrice}
            remaining={remaining}
            isAvailable={isAvailable}
            status={data?.status}
          />
        ),
      },
    ],
    [eventName, currentPrice, remaining, isAvailable, data?.status],
  );

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      title="sale_ticket"
      icon={<ShoppingCartOutlined />}
      steps={steps}
      onFinish={handleFinish}
      initialValues={{ quantity: 1 }}
      confirmDisabled={!isAvailable}
      width={580}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SaleTicketModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************
export function showSaleTicketModal({ data, onSuccess } = {}) {
  const container = document.createElement('div');
  document.body.appendChild(container);
  const root = createRoot(container);

  const handleSetShow = visible => {
    if (!visible) {
      root.unmount();
      document.body.removeChild(container);
    }
  };

  root.render(
    <SaleTicketModal show={true} setShow={handleSetShow} data={data} onSuccess={onSuccess} />,
  );
}

// *******************************************************************************
// SALE TICKET FORM CONTENT
// *******************************************************************************
function SaleTicketFormContent({ eventName, currentPrice, remaining, isAvailable, status }) {
  const { form } = useSimpleFormContext();
  const quantity = Form.useWatch('quantity', form) ?? 1;

  return (
    <>
      {!isAvailable && (
        <Alert
          type="warning"
          showIcon
          message={translateX('event_not_available_for_purchase')}
          description={`${translateX('status')}: ${status || '-'}`}
          style={{ marginBottom: 16 }}
        />
      )}

      <Descriptions bordered size="small" column={1} style={{ marginBottom: 20 }}>
        <Descriptions.Item label={translateX('event')}>{eventName}</Descriptions.Item>
        <Descriptions.Item label={translateX('current_price')}>
          {financialService.formatCurrency(currentPrice)}
        </Descriptions.Item>
        <Descriptions.Item label={translateX('remaining_tickets')}>{remaining}</Descriptions.Item>
      </Descriptions>

      <RecipientCard
        name="buyer"
        label="buyer"
        entityProfiles={['customer', 'person', 'company']}
        viewMode="edit"
        data={{}}
      />

      <SimpleSlider
        name="quantity"
        label={`${translateX('quantity')} (max: ${remaining})`}
        min={1}
        max={Math.max(remaining, 1)}
        marks={{ 1: '1', [Math.max(remaining, 1)]: String(Math.max(remaining, 1)) }}
        required
      />

      <Row justify="end" style={{ marginTop: -8, marginBottom: 16 }}>
        <InputNumber
          min={1}
          max={Math.max(remaining, 1)}
          value={quantity}
          onChange={val => form?.setFieldValue('quantity', val || 1)}
          style={{ width: 100 }}
        />
      </Row>

      <SimpleInput name="note" label="note" placeholder="optional" />
    </>
  );
}

